import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { POIDto } from '@pages/poi-page/interfaces';
import { Coordinates } from '@features/map/interfaces';
import { debounceTime, merge } from 'rxjs';
import { AddressSearchFormService } from './map-address-search-form-service';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '@shared/pipes/pipes.module';
import { OnCallSiteDto } from '@pages/settings-page/on-call-settings/interfaces';
import { DvButtonComponent } from '@devinco/ui/dds';

@Component({
  imports: [
    TranslateModule,
    DvDynamicFormModule,
    PipesModule,
    DvButtonComponent,
  ],
  selector: 'app-map-address-search-form',
  templateUrl: './map-address-search-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./map-address-search-form.component.scss'],
})
export class MapAddressSearchFormComponent implements OnInit, OnChanges {
  private elementsService = inject(AddressSearchFormService);

  @Input() address: Partial<POIDto> | Partial<OnCallSiteDto> | undefined;
  @Input() titleKey: string;

  @Output() confirmBtnClicked = new EventEmitter<Partial<POIDto>>();
  @Output() coordinatesChanged = new EventEmitter<Coordinates>();
  @Output() addressOrPostalPlaceChanged = new EventEmitter<
    Pick<POIDto, 'Address' | 'PostalPlace'>
  >();

  formGroup: FormGroup<FormOf<POIDto>>;
  elements: DvDynamicFormElement[];

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.elements = this.elementsService.formElements;
    this.formGroup = DvFormUtils.createFormGroup(this.elements, true);
    if (this.address) {
      this.formGroup.patchValue(this.address);
    }
  }

  ngOnInit(): void {
    this.listenToAddressOrPostalPlaceChanges();
    this.listenToLatitudeAndLongitudeChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('address' in changes) {
      this.formGroup.patchValue(this.address!, { emitEvent: false });
    }
    if ('address' in changes && !changes['address'].isFirstChange()) {
      this.formGroup.markAsDirty();
    }
  }

  onConfirmClicked(formData: Partial<POIDto> | undefined): void {
    this.confirmBtnClicked.emit(formData);
  }

  private listenToLatitudeAndLongitudeChanges(): void {
    const { GPSLat, GPSLon } = this.formGroup.controls;

    merge(GPSLat.valueChanges, GPSLon.valueChanges)
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (GPSLat.value && GPSLon.value) {
          this.coordinatesChanged.emit({
            lat: GPSLat.value,
            lng: GPSLon.value,
          });
        }
      });
  }

  private listenToAddressOrPostalPlaceChanges(): void {
    const { Address, PostalPlace } = this.formGroup.controls;

    merge(Address!.valueChanges, PostalPlace!.valueChanges)
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.addressOrPostalPlaceChanged.emit({
          Address: Address!.value,
          PostalPlace: PostalPlace!.value,
        });
      });
  }
}
